// src/contexts/UserContext.js - Updated with collaborator multi-workspace support
import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc, 
  collection, 
  getDocs, 
  addDoc,
  serverTimestamp 
} from 'firebase/firestore';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://southamerica-east1-drecenna.cloudfunctions.net/api';

// Create the context
const UserContext = createContext();

// Custom hook to use the user context
export const useUserContext = () => useContext(UserContext);

// Provider component
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState('Usuário');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  
  // Estado para tipo de conta ativa
  const [activeAccountType, setActiveAccountType] = useState(() => {
    try {
      return localStorage.getItem('active_account_type') || 'business';
    } catch (error) {
      return 'business';
    }
  });
  
  // Estado para perfis empresariais (incluindo perfil pessoal na nova estrutura)
  const [businessProfiles, setBusinessProfiles] = useState([]);
  const [activeBusinessProfileId, setActiveBusinessProfileId] = useState(() => {
    try {
      return localStorage.getItem('active_business_profile_id') || null;
    } catch (error) {
      return null;
    }
  });
  
  // Estado para a categorização automática de bancos
  const [automaticBankCategorizationOFX, setAutomaticBankCategorizationOFX] = useState(() => {
    try {
      return localStorage.getItem('automaticBankCategorizationOFX') !== 'false';
    } catch (error) {
      return true;
    }
  });
  
  // Estado para o gatilho de remontagem
  const [accountSwitchTrigger, setAccountSwitchTrigger] = useState(0);

  // NOVO: Estado para evitar múltiplas criações de perfil padrão
  const [defaultProfileCreationInProgress, setDefaultProfileCreationInProgress] = useState(false);

  // NOVO: Estado para indicar se o usuário é um colaborador
  const [isCollaborator, setIsCollaborator] = useState(false);
  const [ownedByCollaboratorProfiles, setOwnedByCollaboratorProfiles] = useState([]);
  const [collaboratorWorkspaces, setCollaboratorWorkspaces] = useState([]);
  const [isWorkspaceSwitching, setIsWorkspaceSwitching] = useState(false);

  // Verificar se o usuário tem conta pessoal (derivado dos perfis empresariais)
  const hasPersonalAccount = useMemo(() => {
    return businessProfiles.some(profile => profile.profileType === 'personal');
  }, [businessProfiles]);

  // Monitor auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setUserName(currentUser.displayName || 'Usuário');
        
        try {
          // Get user document from Firestore
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Check if user is a collaborator
            const isUserCollaborator = userData.isCollaborator === true;
            setIsCollaborator(isUserCollaborator);
            
            // Check subscription status from server API with enhanced info
            let subscriptionData;
            try {
              const response = await axios.get(`${API_URL}/subscription-status?uid=${currentUser.uid}`);
              subscriptionData = response.data;
              
              // Enhanced subscription data with cancellation info
              const enhancedSubscriptionData = {
                ...subscriptionData,
                // Add cancellation fields from Firestore if available
                cancelRequested: userData.subscriptionCancelRequested || subscriptionData.isCanceled || false,
                cancelRequestedAt: userData.subscriptionCancelRequestedAt?.toDate() || null,
                cancelAtPeriodEnd: userData.cancelAtPeriodEnd || subscriptionData.cancelAtPeriodEnd || false,
                isCanceled: subscriptionData.isCanceled || userData.isCanceled || false,
                
                // Derived field for easier checking
                isActiveButCanceled: subscriptionData.active && (subscriptionData.isCanceled || subscriptionData.cancelAtPeriodEnd),
                
                // Enhanced cancellation info
                willCancelAtPeriodEnd: userData.cancelAtPeriodEnd || subscriptionData.cancelAtPeriodEnd || false,
                userRequestedCancellation: userData.subscriptionCancelRequested || false,
                
                // Error handling
                isFallbackData: false
              };
              
              setSubscription(enhancedSubscriptionData);
            } catch (apiError) {
              console.warn('Error fetching subscription status from API:', apiError);
              
              // Fallback to Firestore data with cancellation info
              subscriptionData = {
                active: userData.subscriptionStatus === 'active' && !isSubscriptionExpired(userData.currentPeriodEnd?.toDate()),
                status: userData.subscriptionStatus || 'none',
                planType: userData.planType || null,
                currentPeriodEnd: userData.currentPeriodEnd?.toDate?.() || null,
                cancelRequested: userData.subscriptionCancelRequested || false,
                cancelRequestedAt: userData.subscriptionCancelRequestedAt?.toDate() || null,
                cancelAtPeriodEnd: userData.cancelAtPeriodEnd || false,
                isCanceled: userData.subscriptionStatus === 'canceled' || userData.cancelAtPeriodEnd || userData.isCanceled || false,
                stripeStatus: userData.stripeStatus || userData.subscriptionStatus,
                isActiveButCanceled: (userData.subscriptionStatus === 'active' && !isSubscriptionExpired(userData.currentPeriodEnd?.toDate())) && 
                                   (userData.cancelAtPeriodEnd || userData.isCanceled),
                willCancelAtPeriodEnd: userData.cancelAtPeriodEnd || false,
                userRequestedCancellation: userData.subscriptionCancelRequested || false,
                isFallbackData: true,
                daysRemaining: userData.currentPeriodEnd ? 
                  Math.ceil((userData.currentPeriodEnd.toDate().getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 0
              };
              setSubscription(subscriptionData);
            }
            
            // If this is a collaborator account, handle specially
            if (isUserCollaborator) {
              console.log('[USER CONTEXT] User is a collaborator, loading access data');
              
              // ENHANCED: Check for collaboratorWorkspaces array first
              if (userData.collaboratorWorkspaces && Array.isArray(userData.collaboratorWorkspaces)) {
                console.log('[USER CONTEXT] Found collaboratorWorkspaces array with', 
                            userData.collaboratorWorkspaces.length, 'workspaces');
                
                // Load all workspaces data
                const workspacesData = [];
                
                for (const workspace of userData.collaboratorWorkspaces) {
                  if (workspace.ownerUid && workspace.profileId) {
                    try {
                      // Load the workspace profile data
                      const profileRef = doc(db, "users", workspace.ownerUid, 
                                            "businessProfiles", workspace.profileId);
                      const profileSnap = await getDoc(profileRef);
                      
                      if (profileSnap.exists()) {
                        const profileData = profileSnap.data();
                        
                        // Check if the user is still a member
                        if (profileData.members && profileData.members[currentUser.uid]) {
                          const workspaceWithOwner = {
                            ...profileData,
                            id: profileSnap.id,
                            userId: workspace.ownerUid,
                            ownerUid: workspace.ownerUid,
                            role: profileData.members[currentUser.uid],
                            isCollaboratorWorkspace: true
                          };
                          
                          workspacesData.push(workspaceWithOwner);
                        }
                      }
                    } catch (error) {
                      console.error('[USER CONTEXT] Error loading workspace:', workspace.profileId, error);
                    }
                  }
                }
                
                if (workspacesData.length > 0) {
                  // Set the collaborator workspaces
                  setCollaboratorWorkspaces(workspacesData);
                  
                  // Add to business profiles as well to maintain compatibility
                  setBusinessProfiles(prevProfiles => {
                    const existingIds = new Set(prevProfiles.map(p => `${p.userId}-${p.id}`));
                    const newProfiles = [...prevProfiles];
                    
                    workspacesData.forEach(workspace => {
                      const workspaceKey = `${workspace.userId}-${workspace.id}`;
                      if (!existingIds.has(workspaceKey)) {
                        newProfiles.push(workspace);
                      }
                    });
                    
                    return newProfiles;
                  });
                  
                  // Set the active profile
                  let shouldSetActive = false;
                  let activeProfileIdToUse = activeBusinessProfileId;
                  
                  // If no active profile or the active profile is not in the list, use the first one
                  if (!activeBusinessProfileId || 
                      !workspacesData.some(w => w.id === activeBusinessProfileId)) {
                    activeProfileIdToUse = workspacesData[0].id;
                    shouldSetActive = true;
                  }
                  
                  if (shouldSetActive) {
                    setActiveBusinessProfileId(activeProfileIdToUse);
                    localStorage.setItem('active_business_profile_id', activeProfileIdToUse);
                    
                    // Set to business account type
                    setActiveAccountType('business');
                    localStorage.setItem('active_account_type', 'business');
                  }
                }
              } else {
                // Legacy support: If no collaboratorWorkspaces array but has original invitation
                const originalInvitation = userData.originalInvitation || {};
                const invitedBy = userData.invitedBy;
                const invitedProfileId = originalInvitation.profileId;
                
                if (invitedBy && invitedProfileId) {
                  try {
                    const invitedProfileRef = doc(db, "users", invitedBy, "businessProfiles", invitedProfileId);
                    const invitedProfileSnap = await getDoc(invitedProfileRef);
                    
                    if (invitedProfileSnap.exists()) {
                      const profileData = invitedProfileSnap.data();
                      
                      // Check if the user is still a member
                      if (profileData.members && profileData.members[currentUser.uid]) {
                        const workspaceWithOwner = {
                          ...profileData,
                          id: invitedProfileSnap.id,
                          userId: invitedBy, // The owner's ID
                          ownerUid: invitedBy,
                          role: profileData.members[currentUser.uid],
                          isCollaboratorWorkspace: true,
                          isOriginalInvitation: true
                        };
                        
                        // Update the user document to add this to the collaboratorWorkspaces array
                        try {
                          // CÓDIGO CORRIGIDO: Dividir a atualização em duas partes
                          // Primeiro, atualizar todos os campos exceto o array
                          await updateDoc(doc(db, 'users', currentUser.uid), {
                            updatedAt: new Date() // Use Date() em vez de serverTimestamp()
                          });
                          
                          // Então, atualizar o array separadamente com objetos Date em vez de serverTimestamp
                          await updateDoc(doc(db, 'users', currentUser.uid), {
                            collaboratorWorkspaces: [{
                              ownerUid: invitedBy,
                              profileId: invitedProfileId,
                              workspaceName: profileData.profileName || originalInvitation.workspaceName || 'Workspace',
                              invitedAt: new Date().toISOString(), // CORRIGIDO: Usando string ISO em vez de serverTimestamp()
                              role: profileData.members[currentUser.uid]
                            }]
                          });
                          
                          console.log('[USER CONTEXT] Created collaboratorWorkspaces array from legacy data');
                        } catch (updateError) {
                          console.error('[USER CONTEXT] Error updating user with collaboratorWorkspaces:', updateError);
                        }
                        
                        // Add to collaborator workspaces
                        setCollaboratorWorkspaces([workspaceWithOwner]);
                        
                        // Also add to business profiles
                        setBusinessProfiles(prevProfiles => [workspaceWithOwner, ...prevProfiles]);
                        
                        // Set this as the active profile
                        setActiveBusinessProfileId(invitedProfileSnap.id);
                        localStorage.setItem('active_business_profile_id', invitedProfileSnap.id);
                        
                        // Set to business account type
                        setActiveAccountType('business');
                        localStorage.setItem('active_account_type', 'business');
                      }
                    }
                  } catch (error) {
                    console.error('[USER CONTEXT] Error loading invited workspace:', error);
                  }
                }
              }
            } else {
              // For regular users, handle normally
              // Verificar o tipo de plano
              const hasPro = subscriptionData?.planType === 'business_pro';
              
              // Verificar o tipo de conta armazenado
              const storedAccountType = localStorage.getItem('active_account_type');
              
              // CORRIGIDO: Carregar perfis empresariais ANTES de tomar decisões
              console.log('[USER CONTEXT] Carregando perfis empresariais...');
              await loadBusinessProfiles(currentUser.uid, false); // false = não criar perfil padrão automaticamente
              
              // NOVA LÓGICA: Aguardar os perfis serem carregados antes de determinar o tipo de conta
              const currentProfiles = await getCurrentProfiles(currentUser.uid);
              const hasBusinessProfiles = currentProfiles.some(p => p.profileType === 'business' || !p.profileType);
              const hasPersonalProfile = currentProfiles.some(p => p.profileType === 'personal');
              
              console.log('[USER CONTEXT] Perfis carregados:', {
                totalProfiles: currentProfiles.length,
                hasBusinessProfiles,
                hasPersonalProfile,
                storedAccountType
              });
              
              // Determinar o tipo de conta ativa
              let finalAccountType = storedAccountType;
              
              if (!storedAccountType) {
                // Novo usuário: usar business se tiver perfil empresarial, senão usar personal se tiver
                if (hasBusinessProfiles) {
                  finalAccountType = 'business';
                  setActiveAccountType('business');
                  localStorage.setItem('active_account_type', 'business');
                } else if (hasPersonalProfile && hasPro) {
                  finalAccountType = 'personal';
                  setActiveAccountType('personal');
                  localStorage.setItem('active_account_type', 'personal');
                } else {
                  // Se não tem nenhum perfil, criar um empresarial padrão
                  finalAccountType = 'business';
                  setActiveAccountType('business');
                  localStorage.setItem('active_account_type', 'business');
                  
                  // CORRIGIDO: Criar perfil padrão apenas se necessário
                  const newProfileId = await ensureDefaultBusinessProfile(currentUser.uid, currentUser.displayName);
                  if (newProfileId) {
                    await loadBusinessProfiles(currentUser.uid, false);
                  }
                }
              } else {
                // Se estiver em uma conta pessoal mas não é pro, forçar para business
                if (storedAccountType === 'personal' && !hasPro) {
                  console.log('[USER CONTEXT] Forçando para conta business: usuário não tem plano business_pro');
                  finalAccountType = 'business';
                  setActiveAccountType('business');
                  localStorage.setItem('active_account_type', 'business');
                } 
                // Se estiver em conta pessoal, mas não tem perfil pessoal, forçar business
                else if (storedAccountType === 'personal' && !hasPersonalProfile) {
                  console.log('[USER CONTEXT] Forçando para conta business: usuário não tem perfil pessoal');
                  finalAccountType = 'business';
                  setActiveAccountType('business');
                  localStorage.setItem('active_account_type', 'business');
                }
                // Se estiver em conta empresarial, mas não tem perfil empresarial, criar um
                else if (storedAccountType === 'business' && !hasBusinessProfiles) {
                  console.log('[USER CONTEXT] Criando perfil empresarial: usuário quer usar conta business mas não tem perfil');
                  finalAccountType = 'business';
                  setActiveAccountType('business');
                  localStorage.setItem('active_account_type', 'business');
                  
                  // CORRIGIDO: Criar perfil padrão com nome atual do usuário
                  const newProfileId = await ensureDefaultBusinessProfile(currentUser.uid, currentUser.displayName);
                  if (newProfileId) {
                    await loadBusinessProfiles(currentUser.uid, false);
                  }
                }
                else {
                  // Usar a seleção do usuário se for válida
                  finalAccountType = storedAccountType;
                  setActiveAccountType(storedAccountType);
                }
              }
              
              // CORRIGIDO: Recarregar perfis após qualquer criação
              const updatedProfiles = await getCurrentProfiles(currentUser.uid);
              setBusinessProfiles(updatedProfiles);
              
              // Verificar o perfil empresarial ativo
              const storedProfileId = localStorage.getItem('active_business_profile_id');
              if (finalAccountType === 'business') {
                const businessProfile = updatedProfiles.find(p => 
                  p.profileType === 'business' || !p.profileType);
                
                if (businessProfile) {
                  // Se há perfis empresariais, usar o primeiro válido ou o armazenado
                  if (storedProfileId && updatedProfiles.some(p => p.id === storedProfileId && (p.profileType === 'business' || !p.profileType))) {
                    setActiveBusinessProfileId(storedProfileId);
                  } else {
                    setActiveBusinessProfileId(businessProfile.id);
                    localStorage.setItem('active_business_profile_id', businessProfile.id);
                  }
                }
              } else if (finalAccountType === 'personal') {
                // Para tipo de conta pessoal, verificar se temos um perfil pessoal ativo
                const personalProfile = updatedProfiles.find(p => p.profileType === 'personal');
                if (personalProfile) {
                  setActiveBusinessProfileId(personalProfile.id);
                  localStorage.setItem('active_business_profile_id', personalProfile.id);
                } else {
                  console.log('[USER CONTEXT] Perfil pessoal não encontrado, mas tipo de conta é pessoal');
                }
              }
            }
            
            // Carregar preferências do usuário
            await loadUserPreferences(currentUser.uid);
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          // Em caso de erro, definir como business para segurança
          setActiveAccountType('business');
          localStorage.setItem('active_account_type', 'business');
        }
      } else {
        setUser(null);
        setUserName('Usuário');
        setSubscription(null);
        setBusinessProfiles([]);
        setActiveBusinessProfileId(null);
        setIsCollaborator(false);
        setCollaboratorWorkspaces([]);
      }
      
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Helper function to check if subscription is expired
  const isSubscriptionExpired = (expirationDate) => {
    if (!expirationDate) return false;
    const now = new Date();
    return expirationDate < now;
  };
  
  // Helper function to get current profiles from Firestore
  const getCurrentProfiles = async (userId) => {
    try {
      const profilesRef = collection(db, "users", userId, "businessProfiles");
      const profilesSnap = await getDocs(profilesRef);
      const profiles = [];
      
      profilesSnap.forEach(doc => {
        profiles.push({
          id: doc.id,
          userId: userId,
          ...doc.data()
        });
      });
      
      return profiles;
    } catch (error) {
      console.error('Erro ao obter perfis atuais:', error);
      return [];
    }
  };
  
  // Modified loadBusinessProfiles function to handle collaborators
  const loadBusinessProfiles = async (userId, shouldCreateDefault = false) => {
    try {
      console.log('[USER CONTEXT] loadBusinessProfiles chamada com shouldCreateDefault:', shouldCreateDefault);
      
      // First, get the user document to check if they're a collaborator
      const userDocRef = doc(db, "users", userId);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.exists() ? userDocSnap.data() : {};
      const isUserCollaborator = userData.isCollaborator === true;
      
      // Get the user's own profiles
      const profilesRef = collection(db, "users", userId, "businessProfiles");
      const profilesSnap = await getDocs(profilesRef);
      
      let profiles = [];
      
      // Load the user's own profiles
      profilesSnap.forEach(doc => {
        profiles.push({
          id: doc.id,
          userId: userId,
          ...doc.data(),
          isOwnedByUser: true
        });
      });
      
      // If user is a collaborator, load the workspace they were invited to
      if (isUserCollaborator) {
        // First check the new collaboratorWorkspaces array
        if (userData.collaboratorWorkspaces && Array.isArray(userData.collaboratorWorkspaces)) {
          const workspacesData = [];
          
          for (const workspace of userData.collaboratorWorkspaces) {
            if (workspace.ownerUid && workspace.profileId) {
              try {
                // Load the workspace profile data
                const profileRef = doc(db, "users", workspace.ownerUid, 
                                     "businessProfiles", workspace.profileId);
                const profileSnap = await getDoc(profileRef);
                
                if (profileSnap.exists()) {
                  const profileData = profileSnap.data();
                  
                  // Check if the user is still a member
                  if (profileData.members && profileData.members[userId]) {
                    const workspaceWithOwner = {
                      ...profileData,
                      id: profileSnap.id,
                      userId: workspace.ownerUid,
                      ownerUid: workspace.ownerUid,
                      role: profileData.members[userId],
                      isCollaboratorWorkspace: true
                    };
                    
                    workspacesData.push(workspaceWithOwner);
                  }
                }
              } catch (error) {
                console.error('[USER CONTEXT] Error loading workspace:', workspace.profileId, error);
              }
            }
          }
          
          if (workspacesData.length > 0) {
            setCollaboratorWorkspaces(workspacesData);
            profiles = [...profiles, ...workspacesData];
          }
        } else {
          // Legacy support for originalInvitation
          const invitedBy = userData.invitedBy;
          const originalInvitation = userData.originalInvitation || {};
          const invitedProfileId = originalInvitation.profileId;
          
          if (invitedBy && invitedProfileId) {
            try {
              const invitedProfileRef = doc(db, "users", invitedBy, "businessProfiles", invitedProfileId);
              const invitedProfileSnap = await getDoc(invitedProfileRef);
              
              if (invitedProfileSnap.exists()) {
                const profileData = invitedProfileSnap.data();
                
                // Check if the user is still a member
                if (profileData.members && profileData.members[userId]) {
                  const workspaceWithOwner = {
                    ...profileData,
                    id: invitedProfileSnap.id,
                    userId: invitedBy, // The owner's ID
                    ownerUid: invitedBy,
                    role: profileData.members[userId],
                    isCollaboratorWorkspace: true,
                    isOriginalInvitation: true
                  };
                  
                  // Add to the profiles list
                  profiles.push(workspaceWithOwner);
                  
                  // Store separately for collaborator workspaces
                  setCollaboratorWorkspaces([workspaceWithOwner]);
                }
              }
            } catch (error) {
              console.error('[USER CONTEXT] Error loading invited workspace:', error);
            }
          }
        }
      }
      
      console.log('[USER CONTEXT] Perfis carregados:', profiles.map(p => ({ 
        id: p.id, 
        name: p.profileName,
        isCollaboratorWorkspace: p.isCollaboratorWorkspace || false
      })));
      
      // Sort by creation date (most recent first)
      profiles.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
      
      // Prioritize collaborator workspaces for collaborator accounts
      if (isUserCollaborator) {
        const collaboratorWorkspaces = profiles.filter(p => p.isCollaboratorWorkspace);
        const ownedProfiles = profiles.filter(p => !p.isCollaboratorWorkspace);
        
        setBusinessProfiles([...collaboratorWorkspaces, ...ownedProfiles]);
        setOwnedByCollaboratorProfiles(ownedProfiles);
        
        // For collaborators, if there are collaborator workspaces, set the first one as active
        if (collaboratorWorkspaces.length > 0) {
          setActiveBusinessProfileId(collaboratorWorkspaces[0].id);
          localStorage.setItem('active_business_profile_id', collaboratorWorkspaces[0].id);
          setActiveAccountType('business');
          localStorage.setItem('active_account_type', 'business');
        } 
        // Otherwise, if there are owned profiles, use the first one
        else if (ownedProfiles.length > 0) {
          setActiveBusinessProfileId(ownedProfiles[0].id);
          localStorage.setItem('active_business_profile_id', ownedProfiles[0].id);
        }
        // If no profiles at all and shouldCreateDefault is true, create a default profile
        else if (shouldCreateDefault) {
          console.log('[USER CONTEXT] Nenhum perfil encontrado, criando padrão...');
          const currentUser = auth.currentUser;
          await ensureDefaultBusinessProfile(userId, currentUser?.displayName);
        }
      } 
      // For regular users
      else {
        setBusinessProfiles(profiles);
        
        // If there are profiles but no active profile is defined, use the first one
        if (profiles.length > 0 && !activeBusinessProfileId) {
          setActiveBusinessProfileId(profiles[0].id);
          localStorage.setItem('active_business_profile_id', profiles[0].id);
        }
        // If no profiles and shouldCreateDefault is true, create a default profile
        else if (profiles.length === 0 && shouldCreateDefault) {
          console.log('[USER CONTEXT] Nenhum perfil encontrado, criando padrão...');
          const currentUser = auth.currentUser;
          await ensureDefaultBusinessProfile(userId, currentUser?.displayName);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar perfis empresariais:', error);
      setBusinessProfiles([]);
    }
  };
  
  // CORRIGIDO: Função para garantir a existência de um perfil empresarial padrão
  const ensureDefaultBusinessProfile = async (userId, userDisplayName = null) => {
    try {
      // Verificar se já está sendo criado
      if (defaultProfileCreationInProgress) {
        console.log('[USER CONTEXT] Criação de perfil padrão já em progresso, pulando...');
        return null;
      }
      
      setDefaultProfileCreationInProgress(true);
      
      // Verificar se já existe um perfil com ID 'business'
      const businessProfileRef = doc(db, "users", userId, "businessProfiles", "business");
      const businessProfileDoc = await getDoc(businessProfileRef);
      
      if (businessProfileDoc.exists()) {
        console.log('[USER CONTEXT] Perfil "business" já existe, não criando novo');
        setDefaultProfileCreationInProgress(false);
        return "business";
      }
      
      // Obter o displayName mais atual possível
      let displayNameToUse = userDisplayName;
      
      if (!displayNameToUse) {
        // Tentar obter do Firebase Auth
        const currentUser = auth.currentUser;
        if (currentUser?.displayName) {
          displayNameToUse = currentUser.displayName;
        } else {
          // Como último recurso, obter do documento do usuário no Firestore
          const userRef = doc(db, "users", userId);
          const userSnapshot = await getDoc(userRef);
          const userData = userSnapshot.data() || {};
          displayNameToUse = userData.displayName || userData.name || 'Minha Empresa';
        }
      }
      
      console.log('[USER CONTEXT] Criando perfil padrão com nome:', displayNameToUse);
      
      // Criar o perfil padrão
      const defaultProfile = {
        profileName: displayNameToUse,
        profileType: 'business', // Explicitamente marcar como tipo empresarial
        createdAt: new Date(),
        isDefault: true,
        // Inicializar o campo members com o proprietário
        members: {
          [userId]: 'owner'
        }
      };
      
      // Adicionar ao Firestore com ID fixo 'business' para o perfil padrão
      await setDoc(businessProfileRef, defaultProfile);
      
      console.log('[USER CONTEXT] Perfil empresarial padrão criado com sucesso');
      
      setDefaultProfileCreationInProgress(false);
      return "business";
    } catch (error) {
      console.error('[USER CONTEXT] Erro ao criar perfil empresarial padrão:', error);
      setDefaultProfileCreationInProgress(false);
      return null;
    }
  };
  
  // Monitorar mudanças no tipo de conta ativa
  useEffect(() => {
    try {
      localStorage.setItem('active_account_type', activeAccountType);
    } catch (error) {
      console.error('Error saving activeAccountType to localStorage:', error);
    }
  }, [activeAccountType]);
  
  // Monitorar mudanças no perfil empresarial ativo
  useEffect(() => {
    try {
      if (activeBusinessProfileId) {
        localStorage.setItem('active_business_profile_id', activeBusinessProfileId);
      } else {
        localStorage.removeItem('active_business_profile_id');
      }
    } catch (error) {
      console.error('Error saving activeBusinessProfileId to localStorage:', error);
    }
  }, [activeBusinessProfileId]);
  
  // Monitorar mudanças na configuração de categorização automática
  useEffect(() => {
    try {
      localStorage.setItem('automaticBankCategorizationOFX', automaticBankCategorizationOFX.toString());
    } catch (error) {
      console.error('Error saving automaticBankCategorizationOFX to localStorage:', error);
    }
  }, [automaticBankCategorizationOFX]);

  // Função para carregar preferências do usuário
  const loadUserPreferences = async (userId) => {
    try {
      const userPrefsRef = doc(db, "userPreferences", userId);
      const userPrefsDoc = await getDoc(userPrefsRef);
      
      if (userPrefsDoc.exists()) {
        const prefs = userPrefsDoc.data();
        
        // Carregar a configuração de categorização automática se existir
        if (prefs.hasOwnProperty('automaticBankCategorizationOFX')) {
          setAutomaticBankCategorizationOFX(prefs.automaticBankCategorizationOFX);
        }
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  // UNIFICADO: Função para criar perfis (tanto empresariais quanto pessoais)
  const createProfile = async (profileData, profileType = 'business') => {
    if (!user || !user.uid) return null;
    
    try {
      // Verificar limite de perfis com base no plano de assinatura - AJUSTADO
      const maxProfiles = subscription?.planType === 'business_pro' ? 2 : 
                         subscription?.planType === 'business' ? 1 : 1;
      
      // Para perfis pessoais, exigir plano business_pro
      if (profileType === 'personal' && subscription?.planType !== 'business_pro') {
        throw new Error('Apenas usuários com plano Empresarial Pro podem criar contas pessoais.');
      }
      
      // Verificar se já existe um perfil do tipo solicitado
      if (profileType === 'personal' && 
          businessProfiles.some(profile => profile.profileType === 'personal')) {
        throw new Error('Você já possui uma conta pessoal.');
      }
      
      // Verificar limite de perfis empresariais
      if (profileType === 'business' && 
          businessProfiles.filter(p => p.profileType === 'business' || !p.profileType).length >= maxProfiles) {
        throw new Error(`Sua conta não consegue criar mais de ${maxProfiles === 1 ? 'uma empresa' : `${maxProfiles} empresas`}, contate o suporte caso queira adicionar mais empresas.`);
      }
      
      // Determinar o ID do perfil
      let profileId;
      
      if (profileType === 'personal') {
        // Para perfil pessoal, usar o ID fixo "personal"
        profileId = "personal";
      } else {
        // Para perfil empresarial, sanitizar o nome como ID
        const sanitizeForId = (name) => {
          // Remover acentos e caracteres especiais
          const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          // Converter para minúsculas e substituir espaços e caracteres não alfanuméricos por underscores
          return normalized.toLowerCase().trim().replace(/[^a-z0-9]/g, '_');
        };
        
        // Criar ID baseado no nome
        profileId = sanitizeForId(profileData.profileName || 'empresa');
        
        // Verificar se já existe um perfil com esse ID
        const profileExists = businessProfiles.some(profile => profile.id === profileId);
        
        // Se já existir, adicionar um sufixo numérico
        if (profileExists) {
          // Encontrar IDs similares para não criar duplicatas
          const similarIds = businessProfiles
            .map(p => p.id)
            .filter(id => id.startsWith(profileId));
            
          // Adicionar um sufixo numérico
          profileId = `${profileId}_${similarIds.length + 1}`;
        }
      }
      
      // Verificar se já existe um perfil com esse ID exato
      const existingProfile = businessProfiles.find(p => p.id === profileId);
      if (existingProfile) {
        if (profileType === 'personal') {
          throw new Error('Você já possui uma conta pessoal.');
        } else {
          throw new Error(`Já existe um perfil com o ID "${profileId}". Tente outro nome.`);
        }
      }
      
      // Criar o perfil com o ID especificado (em vez de usar o ID gerado pelo Firestore)
      const profileRef = doc(db, `users/${user.uid}/businessProfiles`, profileId);
      
      const fullProfileData = {
        ...profileData,
        profileName: profileData.profileName || (profileType === 'personal' ? 'Conta Pessoal' : 'Minha Empresa'),
        id: profileId,
        userId: user.uid,
        profileType: profileType,
        createdAt: new Date(), // CORRIGIDO: Use Date() em vez de serverTimestamp()
        isDefault: profileType === 'business' && businessProfiles.length === 0,
        // Inicializar o campo members com o proprietário
        members: {
          [user.uid]: 'owner'
        }
      };
      
      await setDoc(profileRef, fullProfileData);
      
      // Se for um perfil pessoal, criar categorias padrão
      if (profileType === 'personal') {
        // Armazenar o ID deste perfil pessoal no documento principal do usuário
        await updateDoc(doc(db, `users/${user.uid}`), { personalProfileId: profileId });
        
        // Criar categorias padrão para uso pessoal
        const categoriesRef = doc(db, "userCategories", user.uid, profileId, "categories");
        await setDoc(categoriesRef, {
          categories: {
            "💸 Receitas.Salário": true,
            "💸 Receitas.Freelance": true,
            "💸 Receitas.Renda extra": true,
            "💸 Receitas.Reembolsos": true,
            "💸 Receitas.Outros Depósitos": true,
            "🧾 Despesas Fixas.Aluguel/Financiamento": true,
            "🧾 Despesas Fixas.Condomínio": true,
            "🧾 Despesas Fixas.Energia": true,
            "🧾 Despesas Fixas.Água": true,
            "🧾 Despesas Fixas.Internet/Telefone": true,
            "🧾 Despesas Fixas.Academia": true,
            "🧾 Despesas Fixas.Assinaturas": true,
            "🧾 Despesas Fixas.Plano de Saúde": true,
            "🧾 Despesas Fixas.Planos Móveis": true,
            "🧾 Despesas Fixas.Cursos": true,
            "🧾 Despesas Fixas.Impostos": true,
            "🍞 Despesas Variáveis.Supermercado": true,
            "🍞 Despesas Variáveis.Farmácia": true,
            "🍞 Despesas Variáveis.Consultas": true,
            "🍞 Despesas Variáveis.Transporte Público": true,
            "🍞 Despesas Variáveis.Transporte por Aplicativo": true,
            "🍞 Despesas Variáveis.Gasolina": true,
            "🍞 Despesas Variáveis.Restaurantes/Lanches": true,
            "🍞 Despesas Variáveis.Lazer": true,
            "🍞 Despesas Variáveis.Roupas": true,
            "🍞 Despesas Variáveis.Cuidados pessoais": true,
            "💳 Pagamentos e Transferências.Investimentos": true,
            "💳 Pagamentos e Transferências.Pagamentos de empréstimos": true,
            "💳 Pagamentos e Transferências.Tarifa bancária": true
          },
          categoryOrder: {
            "💸 Receitas": 1,
            "🧾 Despesas Fixas": 2,
            "🍞 Despesas Variáveis": 3,
            "💳 Pagamentos e Transferências": 4
          },
          businessModel: "pessoal",
          createdAt: new Date() // CORRIGIDO: Use Date() em vez de serverTimestamp()
        });
        
        // Criar mapeamentos vazios para uso pessoal
        const mappingsRef = doc(db, "categoryMappings", user.uid, profileId, "mappings");
        await setDoc(mappingsRef, {
          mappings: {},
          createdAt: new Date() // CORRIGIDO: Use Date() em vez de serverTimestamp()
        });
        
        // Atualizar preferências do usuário
        const userPrefsRef = doc(db, "userPreferences", user.uid);
        await updateDoc(userPrefsRef, {
          hasPersonalAccount: true,
          personalProfileId: profileId,
          updatedAt: new Date() // CORRIGIDO: Use Date() em vez de serverTimestamp()
        }).catch(async (error) => {
          if (error.code === 'not-found') {
            await setDoc(userPrefsRef, {
              hasPersonalAccount: true,
              personalProfileId: profileId,
              createdAt: new Date(), // CORRIGIDO: Use Date() em vez de serverTimestamp()
              updatedAt: new Date() // CORRIGIDO: Use Date() em vez de serverTimestamp()
            });
          } else {
            throw error;
          }
        });
      }
      
      // CORRIGIDO: Atualizar a lista de perfis sem criar um perfil padrão
      await loadBusinessProfiles(user.uid, false);
      
      // Mudar para o novo perfil
      handleSwitchAccountType(profileType, profileId);
      
      return profileId;
    } catch (error) {
      console.error('Erro ao criar perfil:', error);
      throw error;
    }
  };
  
  // CORRIGIDO: Atualizar perfil com melhor handling do estado local
  const updateBusinessProfile = async (profileId, data) => {
    if (!user || !user.uid || !profileId) return false;
    
    try {
      console.log('[USER CONTEXT] updateBusinessProfile chamada:', { profileId, data });
      
      // Verificar se o perfil existe
      const profileRef = doc(db, "users", user.uid, "businessProfiles", profileId);
      const profileDoc = await getDoc(profileRef);
      
      if (!profileDoc.exists()) {
        throw new Error('Perfil não encontrado.');
      }
      
      // CORRIGIDO: Garantir que temos os dados atuais antes da atualização
      const currentProfileData = profileDoc.data();
      console.log('[USER CONTEXT] Dados atuais do perfil:', currentProfileData);
      
      // Preparar dados para atualização
      const updateData = {
        ...data,
        updatedAt: new Date() // CORRIGIDO: Use Date() em vez de serverTimestamp()
      };
      
      console.log('[USER CONTEXT] Dados para atualização:', updateData);
      
      // Atualizar no Firestore
      await updateDoc(profileRef, updateData);
      console.log('[USER CONTEXT] Perfil atualizado no Firestore com sucesso');
      
      // CORRIGIDO: Atualizar imediatamente o estado local
      setBusinessProfiles(prevProfiles => {
        const updatedProfiles = prevProfiles.map(profile => {
          if (profile.id === profileId) {
            const updatedProfile = { 
              ...profile, 
              ...updateData,
              updatedAt: new Date() // CORRIGIDO: Use Date() em vez de serverTimestamp()
            };
            console.log('[USER CONTEXT] Perfil atualizado no estado local:', updatedProfile);
            return updatedProfile;
          }
          return profile;
        });
        
        console.log('[USER CONTEXT] Estado de perfis atualizado');
        return updatedProfiles;
      });
      
      // CORRIGIDO: Forçar re-render dos componentes que dependem dos perfis
      setAccountSwitchTrigger(prev => prev + 1);
      
      return true;
    } catch (error) {
      console.error('[USER CONTEXT] Erro ao atualizar perfil:', error);
      return false;
    }
  };
  
  // Excluir perfil (empresarial ou pessoal)
  const deleteBusinessProfile = async (profileId) => {
    if (!user || !user.uid || !profileId) return false;
    
    try {
      // Verificar se o perfil existe
      const profileRef = doc(db, "users", user.uid, "businessProfiles", profileId);
      const profileDoc = await getDoc(profileRef);
      
      if (!profileDoc.exists()) {
        throw new Error('Perfil não encontrado.');
      }
      
      const profileData = profileDoc.data();
      
      // Impedir a exclusão do único perfil
      if (businessProfiles.length <= 1) {
        throw new Error('Não é possível excluir o único perfil existente. Crie outro perfil antes de excluir este.');
      }
      
      // Impedir a exclusão do perfil pessoal se for o único perfil pessoal
      if (profileData.profileType === 'personal' && 
          businessProfiles.filter(p => p.profileType === 'personal').length <= 1) {
        throw new Error('Não é possível excluir a conta pessoal. Para removê-la, contate o suporte.');
      }
      
      // Verificar se é o perfil ativo
      const isActiveProfile = profileId === activeBusinessProfileId;
      
      // "Soft delete" no Firestore
      await updateDoc(profileRef, {
        isDeleted: true,
        deletedAt: new Date() // CORRIGIDO: Use Date() em vez de serverTimestamp()
      });
      
      // Atualizar o estado local
      const updatedProfiles = businessProfiles.filter(profile => profile.id !== profileId);
      setBusinessProfiles(updatedProfiles);
      
      // Se excluímos o perfil ativo, mudar para outro
      if (isActiveProfile && updatedProfiles.length > 0) {
        // Se excluímos um perfil pessoal, mudar para um perfil empresarial
        if (profileData.profileType === 'personal') {
          const firstBusinessProfile = updatedProfiles.find(p => 
            p.profileType === 'business' || !p.profileType);
          
          if (firstBusinessProfile) {
            setActiveAccountType('business');
            localStorage.setItem('active_account_type', 'business');
            setActiveBusinessProfileId(firstBusinessProfile.id);
            localStorage.setItem('active_business_profile_id', firstBusinessProfile.id);
          }
        } else {
          setActiveBusinessProfileId(updatedProfiles[0].id);
          localStorage.setItem('active_business_profile_id', updatedProfiles[0].id);
        }
        
        // Trigger para remontagem
        setAccountSwitchTrigger(prev => prev + 1);
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao excluir perfil:', error);
      throw error;
    }
  };

  // Função para atualizar a configuração de categorização automática
  const updateAutomaticBankCategorization = async (value) => {
    try {
      setAutomaticBankCategorizationOFX(value);
      localStorage.setItem('automaticBankCategorizationOFX', value.toString());
      
      // Se o usuário estiver autenticado, salvar no Firestore também
      if (user && user.uid) {
        const userPrefsRef = doc(db, "userPreferences", user.uid);
        await updateDoc(userPrefsRef, {
          automaticBankCategorizationOFX: value,
          updatedAt: new Date() // CORRIGIDO: Use Date() em vez de serverTimestamp()
        }).catch(async (error) => {
          if (error.code === 'not-found') {
            await setDoc(userPrefsRef, {
              automaticBankCategorizationOFX: value,
              createdAt: new Date(), // CORRIGIDO: Use Date() em vez de serverTimestamp()
              updatedAt: new Date() // CORRIGIDO: Use Date() em vez de serverTimestamp()
            });
          } else {
            throw error;
          }
        });
      }
      return true;
    } catch (error) {
      console.error('Error updating automatic bank categorization setting:', error);
      return false;
    }
  };
  
  // Helper method to get auth headers for API requests
  const setupAuthHeaders = async () => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Get a fresh token
      const token = await user.getIdToken(true);
      
      return {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
    } catch (error) {
      console.error('Error setting up auth headers:', error);
      throw new Error('Authentication failed');
    }
  };

  // Function to refresh subscription data
  const refreshSubscriptionData = async () => {
    if (!user || !user.uid) return null;
    
    try {
      setSubscriptionLoading(true);
      
      const response = await axios.get(`${API_URL}/subscription-status?uid=${user.uid}`);
      const subscriptionData = response.data;
      
      // Get user data from Firestore for cancellation info
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      
      // Enhanced subscription data with cancellation info
      const enhancedSubscriptionData = {
        ...subscriptionData,
        cancelRequested: userData.subscriptionCancelRequested || subscriptionData.isCanceled || false,
        cancelRequestedAt: userData.subscriptionCancelRequestedAt?.toDate() || null,
        cancelAtPeriodEnd: userData.cancelAtPeriodEnd || subscriptionData.cancelAtPeriodEnd || false,
        isCanceled: subscriptionData.isCanceled || userData.isCanceled || false,
        isActiveButCanceled: subscriptionData.active && (subscriptionData.isCanceled || subscriptionData.cancelAtPeriodEnd),
        willCancelAtPeriodEnd: userData.cancelAtPeriodEnd || subscriptionData.cancelAtPeriodEnd || false,
        userRequestedCancellation: userData.subscriptionCancelRequested || false,
        isFallbackData: false
      };
      
      setSubscription(enhancedSubscriptionData);
      return enhancedSubscriptionData;
    } catch (error) {
      console.error('Error refreshing subscription data:', error);
      return null;
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // MODIFICADO: Função para alternar entre conta empresarial/pessoal E entre perfis empresariais
  const handleSwitchAccountType = (type, profileIdToSwitchTo = null) => {
    if (activeAccountType === type) {
      if (type === 'business' && activeBusinessProfileId === profileIdToSwitchTo) {
        console.log("Já está na conta/perfil selecionado.");
        return true; // Já estamos no tipo/perfil desejado
      } else if (type === 'personal') {
        console.log("Já está na conta pessoal.");
        return true; // Já estamos na conta pessoal
      }
    }
    
    // Validar mudança para conta pessoal
    if (type === 'personal') {
      if (subscription?.planType !== 'business_pro') {
        console.warn('Não é possível mudar para conta pessoal: plano não é business_pro');
        return false;
      }
      
      // Verificar se existe algum perfil do tipo 'personal'
      const hasPersonalProfileInBusinessProfiles = businessProfiles.some(
        profile => profile.profileType === 'personal'
      );
      
      if (!hasPersonalProfileInBusinessProfiles) {
        console.warn('Não é possível mudar para conta pessoal: perfil pessoal não existe');
        return false;
      }
    }
    
    try {
      console.log(`Trocando para a conta: ${type}${type === 'business' && profileIdToSwitchTo ? `, perfil: ${profileIdToSwitchTo}` : ''}`);
      
      setLoading(true);
      
      // 1. Salvar o novo tipo de conta no localStorage
      localStorage.setItem('active_account_type', type);

      // 2. Atualizar o estado do tipo de conta ativo
      setActiveAccountType(type);

      // 3. Gerenciar o perfil empresarial ativo
      if (type === 'personal') {
        // Se estamos indo para conta pessoal, encontrar o perfil pessoal
        // No novo sistema, o perfil pessoal sempre terá ID "personal"
        const personalProfile = businessProfiles.find(p => p.id === "personal" || p.profileType === 'personal');
        
        if (personalProfile) {
          // Se um ID específico foi fornecido, verificar se é um perfil pessoal
          if (profileIdToSwitchTo) {
            const specificPersonalProfile = businessProfiles.find(
              p => p.id === profileIdToSwitchTo && p.profileType === 'personal'
            );
            
            if (specificPersonalProfile) {
              setActiveBusinessProfileId(profileIdToSwitchTo);
              localStorage.setItem('active_business_profile_id', profileIdToSwitchTo);
            } else {
              // Se o ID fornecido não for um perfil pessoal, usar o perfil pessoal encontrado
              setActiveBusinessProfileId(personalProfile.id);
              localStorage.setItem('active_business_profile_id', personalProfile.id);
            }
          } else {
            // Se nenhum ID específico foi fornecido, usar o perfil pessoal encontrado
            setActiveBusinessProfileId(personalProfile.id);
            localStorage.setItem('active_business_profile_id', personalProfile.id);
          }
        } else {
          // Se não encontrou perfil pessoal, não deveria ter chegado aqui (validado acima)
          console.warn('Erro: Conta pessoal selecionada, mas nenhum perfil pessoal encontrado');
          setActiveAccountType('business');
          localStorage.setItem('active_account_type', 'business');
          
          // Selecionar um perfil empresarial
          const businessProfile = businessProfiles.find(p => 
            p.profileType === 'business' || !p.profileType);
          
          if (businessProfile) {
            setActiveBusinessProfileId(businessProfile.id);
            localStorage.setItem('active_business_profile_id', businessProfile.id);
          }
        }
      } else if (type === 'business') {
        // Se estamos indo para conta empresarial com um perfil específico
        if (profileIdToSwitchTo) {
          // Verificar se o perfil existe e é do tipo empresarial
          const businessProfile = businessProfiles.find(
            p => p.id === profileIdToSwitchTo && 
            (p.profileType === 'business' || !p.profileType)
          );
          
          if (businessProfile) {
            setActiveBusinessProfileId(profileIdToSwitchTo);
            localStorage.setItem('active_business_profile_id', profileIdToSwitchTo);
          } else {
            console.warn(`ID de perfil inválido ou não é um perfil empresarial: ${profileIdToSwitchTo}`);
            
            // Usar o primeiro perfil empresarial disponível como fallback
            const fallbackProfile = businessProfiles.find(p => 
              p.profileType === 'business' || !p.profileType);
              
            if (fallbackProfile) {
              setActiveBusinessProfileId(fallbackProfile.id);
              localStorage.setItem('active_business_profile_id', fallbackProfile.id);
            }
          }
        } 
        // Se não especificou perfil, mas não tem nenhum ativo, selecionar o primeiro perfil empresarial
        else if (!activeBusinessProfileId) {
          const businessProfile = businessProfiles.find(p => 
            p.profileType === 'business' || !p.profileType);
            
          if (businessProfile) {
            setActiveBusinessProfileId(businessProfile.id);
            localStorage.setItem('active_business_profile_id', businessProfile.id);
          }
        }
        // Se já tem um perfil ativo, mas é um perfil pessoal, mudar para um perfil empresarial
        else {
          const activeProfile = businessProfiles.find(p => p.id === activeBusinessProfileId);
          
          if (activeProfile && activeProfile.profileType === 'personal') {
            const businessProfile = businessProfiles.find(p => 
              p.profileType === 'business' || !p.profileType);
              
            if (businessProfile) {
              setActiveBusinessProfileId(businessProfile.id);
              localStorage.setItem('active_business_profile_id', businessProfile.id);
            }
          }
        }
      }

      // 4. Incrementar o gatilho para forçar a remontagem dos componentes
      setAccountSwitchTrigger(prevTrigger => prevTrigger + 1);
      setLoading(false);
      
      return true;
    } catch (error) {
      console.error("Erro ao trocar de conta:", error);
      setLoading(false);
      return false;
    }
  };
  
  // Function to switch between collaborator workspaces
 const switchCollaboratorWorkspace = async (workspaceId, ownerUid) => {
  if (!user || !workspaceId || isWorkspaceSwitching) return false;
  
  try {
    setIsWorkspaceSwitching(true);
    
    // Find the workspace in the collaborator workspaces
    // If ownerUid is provided, use it to disambiguate between workspaces with the same ID
    const workspace = ownerUid 
      ? collaboratorWorkspaces.find(w => w.id === workspaceId && w.ownerUid === ownerUid)
      : collaboratorWorkspaces.find(w => w.id === workspaceId);
    
    if (!workspace) {
      console.error('[USER CONTEXT] Workspace not found:', { workspaceId, ownerUid });
      setIsWorkspaceSwitching(false);
      return false;
    }
    
    console.log('[USER CONTEXT] Switching to workspace:', workspace.profileName || workspace.workspaceName);
    console.log('[USER CONTEXT] Workspace details:', {
      id: workspace.id, 
      profileId: workspace.profileId,
      ownerUid: workspace.ownerUid,
      name: workspace.profileName || workspace.workspaceName
    });
    
    // Store both the profile ID and owner ID in localStorage to properly identify the workspace
    setActiveBusinessProfileId(workspaceId);
    localStorage.setItem('active_business_profile_id', workspaceId);
    localStorage.setItem('active_workspace_owner_uid', workspace.ownerUid);
    
    // Ensure account type is business
    setActiveAccountType('business');
    localStorage.setItem('active_account_type', 'business');
    
    // FORCE CLEAR ANY CACHED DATA
    localStorage.removeItem('cached_transactions');
    localStorage.removeItem('cached_categories');
    localStorage.removeItem('cached_mappings');
    
    // Trigger a remount
    setAccountSwitchTrigger(prev => prev + 1);
    
    // Force reload the page to ensure all components are refreshed
    // This is a temporary solution until the caching issue can be properly fixed
    setTimeout(() => {
      window.location.reload();
    }, 300);
    
    setIsWorkspaceSwitching(false);
    return true;
  } catch (error) {
    console.error('[USER CONTEXT] Error switching workspace:', error);
    setIsWorkspaceSwitching(false);
    return false;
  }
};

  // MELHORADO: Função para verificar nível de acesso com suporte a assinaturas canceladas
  const hasAccess = (requiredAccessLevel) => {
    // For collaborator accounts, allow access to most features
    if (isCollaborator) {
      return true;
    }
    
    if (!subscription) return false;
    
    // NOVA LÓGICA: Verificar se a assinatura está ativa, mesmo que cancelada
    const isCurrentlyActive = subscription.active && !subscription.isExpired;
    
    if (!isCurrentlyActive) {
      console.log('[USER CONTEXT] Access denied: subscription not active or expired');
      return false;
    }
    
    // Se a assinatura está ativa mas cancelada, ainda permitir acesso até expirar
    if (subscription.isActiveButCanceled) {
      console.log('[USER CONTEXT] Access granted: subscription is canceled but still active until period end');
    }
    
    // Verificação para "personal_account"
    if (requiredAccessLevel === 'personal_account') {
      return subscription.planType === 'business_pro';
    }
    
    // Only check against business plans when using the business account
    if (activeAccountType === 'business') {
      if (requiredAccessLevel === 'business') {
        return ['business', 'business_pro'].includes(subscription.planType);
      }
      if (requiredAccessLevel === 'business_pro') {
        return subscription.planType === 'business_pro';
      }
    }
    
    // For personal account, check personal plan
    if (activeAccountType === 'personal') {
      // Para conta pessoal, verificar se tem plano business_pro (condição necessária)
      return subscription.planType === 'business_pro';
    }
    
    return false;
  };

  // MODIFICADO: Função para obter o subcaminho adequado para documentos
  const getAccountSubpath = () => {
    // If we're accessing a collaborator workspace, use that ID
    if (isCollaborator && activeBusinessProfileId && collaboratorWorkspaces.some(w => w.id === activeBusinessProfileId)) {
      return activeBusinessProfileId;
    }
    
    if (activeAccountType === 'personal') {
      // Para conta pessoal, retornar "personal"
      return 'personal';
    } else if (activeBusinessProfileId) {
      // Para conta empresarial, retornar o ID do perfil ativo
      return activeBusinessProfileId;
    }
    
    // Fallback para perfil padrão "business"
    return 'business';
  };

  // FIXED: Method to check if current user is a member or owner of a profile
  const checkProfileAccess = (profileId, userId = user?.uid) => {
    if (!profileId || !userId) return { isMember: false, isOwner: false };
    
    // Find the profile
    const profile = businessProfiles.find(p => p.id === profileId);
    if (!profile) return { isMember: false, isOwner: false };
    
    // FIXED: Special case for collaborator workspaces
    // If this profile has isCollaboratorWorkspace flag, use the role field directly
    if (profile.isCollaboratorWorkspace) {
      return {
        isMember: true,
        isOwner: profile.role === 'owner', // This will be 'collaborator' for non-owners
        role: profile.role || null,
        isCollaboratorView: true
      };
    }
    
    // Check if it's the default profile (backward compatibility)
    if (profile.isDefault && userId === user.uid) {
      return { isMember: true, isOwner: true };
    }
    
    // Check members field
    const members = profile.members || {};
    const userRole = members[userId];
    
    return {
      isMember: !!userRole,
      isOwner: userRole === 'owner',
      role: userRole || null
    };
  };

  // Função para migrar dados para nova estrutura se necessário
  const migrateToNewStructure = async (userId) => {
    if (!userId) return { success: false, error: "ID de usuário é obrigatório" };
    
    try {
      // 1. Verificar se já existe um perfil com ID 'business'
      const businessProfileRef = doc(db, "users", userId, "businessProfiles", "business");
      const businessProfileDoc = await getDoc(businessProfileRef);
      
      if (businessProfileDoc.exists()) {
        console.log("Perfil 'business' já existe, pulando migração.");
        return { 
          success: true, 
          message: "Perfil 'business' já existe"
        };
      }
      
      // 2. Verificar se existem dados no caminho antigo
      // Verificar se existem categorias
      const oldCategoriesRef = doc(db, "userCategories", userId);
      const oldCategoriesDoc = await getDoc(oldCategoriesRef);
      
      // Verificar se existem mapeamentos
      const oldMappingsRef = doc(db, "categoryMappings", userId);
      const oldMappingsDoc = await getDoc(oldMappingsRef);
      
      // Verificar se existem transações
      const oldTransactionsRef = collection(db, "ofxFiles", userId, "business");
      const oldTransactionsSnapshot = await getDocs(oldTransactionsRef);
      
      const hasOldData = oldCategoriesDoc.exists() || 
                        oldMappingsDoc.exists() || 
                        !oldTransactionsSnapshot.empty;
      
      if (!hasOldData) {
        console.log("Não há dados antigos para migrar.");
        return {
          success: true,
          message: "Não há dados para migrar"
        };
      }
      
      // 3. Criar o perfil 'business' padrão
      const userRef = doc(db, "users", userId);
      const userSnapshot = await getDoc(userRef);
      const userData = userSnapshot.data() || {};
      
      // CORRIGIDO: Usar o displayName mais atual do Firebase Auth
      const currentUser = auth.currentUser;
      const currentDisplayName = currentUser?.displayName || userData.displayName || userData.name || "Minha Empresa Principal";
      
      const defaultProfile = {
        profileName: currentDisplayName,
        profileType: 'business',
        createdAt: new Date(), // CORRIGIDO: Use Date() em vez de serverTimestamp()
        isDefault: true,
        isMigrated: true,
        // Initialize members field with owner
        members: {
          [userId]: 'owner'
        }
      };
      
      await setDoc(businessProfileRef, defaultProfile);
      console.log("Perfil 'business' criado para migração com nome:", currentDisplayName);
      
      // 4. Registrar a migração
      await updateDoc(userRef, {
        migrationCompleted: true,
        migrationDate: new Date() // CORRIGIDO: Use Date() em vez de serverTimestamp()
      });
      
      return {
        success: true,
        message: "Migração concluída com sucesso"
      };
    } catch (error) {
      console.error("Erro na migração:", error);
      return {
        success: false,
        error: error.message,
        stack: error.stack
      };
    }
  };

  // Função para recuperação de emergência
  const resetAccountType = () => {
    try {
      // Sempre restaurar para business
      localStorage.setItem('active_account_type', 'business');
      setActiveAccountType('business');
      
      // Se tivermos perfis, selecionar o primeiro
      if (businessProfiles.length > 0) {
        const businessProfile = businessProfiles.find(p => 
          p.profileType === 'business' || !p.profileType);
          
        if (businessProfile) {
          setActiveBusinessProfileId(businessProfile.id);
          localStorage.setItem('active_business_profile_id', businessProfile.id);
        } else {
          setActiveBusinessProfileId(businessProfiles[0].id);
          localStorage.setItem('active_business_profile_id', businessProfiles[0].id);
        }
      } else {
        // Limpar perfil ativo se não houver perfis
        setActiveBusinessProfileId(null);
        localStorage.removeItem('active_business_profile_id');
      }
      
      setAccountSwitchTrigger(prev => prev + 1);
      
      console.log('Restaurado tipo de conta para: business');
      return true;
    } catch (error) {
      console.error('Erro ao redefinir tipo de conta:', error);
      return false;
    }
  };

  // NOVA FUNÇÃO: Verificar se a assinatura está cancelada mas ainda válida
  const isSubscriptionCanceledButActive = () => {
    return subscription && subscription.isActiveButCanceled;
  };

  // NOVA FUNÇÃO: Obter mensagem de status da assinatura
  const getSubscriptionStatusMessage = () => {
    if (!subscription) return 'Nenhuma assinatura encontrada';
    
    if (subscription.isExpired) {
      return 'Assinatura expirada';
    }
    
    if (subscription.isActiveButCanceled) {
      const endDate = new Date(subscription.currentPeriodEnd).toLocaleDateString('pt-BR');
      return `Assinatura cancelada - acesso até ${endDate}`;
    }
    
    if (subscription.active) {
      return 'Assinatura ativa';
    }
    
    return `Status: ${subscription.status}`;
  };

  // NOVA FUNÇÃO: Verificar se pode cancelar a assinatura
  const canCancelSubscription = () => {
    return subscription?.active && 
           !subscription?.isCanceled && 
           !subscription?.cancelAtPeriodEnd &&
           !subscription?.willCancelAtPeriodEnd &&
           subscription?.status === 'active';
  };

  return (
    <UserContext.Provider 
      value={{ 
        user, 
        userName, 
        subscription, 
        subscriptionLoading,
        hasAccess, 
        loading,
        activeAccountType,
        hasPersonalAccount,
        businessProfiles,
        activeBusinessProfileId,
        createProfile,
        updateBusinessProfile,
        deleteBusinessProfile,
        handleSwitchAccountType, 
        getAccountSubpath,
        accountSwitchTrigger,
        automaticBankCategorizationOFX,
        updateAutomaticBankCategorization,
        resetAccountType,
        migrateToNewStructure,
        refreshSubscriptionData,
        setupAuthHeaders,
        checkProfileAccess,
        
        // Collaborator related properties
        isCollaborator,
        collaboratorWorkspaces,
        ownedByCollaboratorProfiles,
        switchCollaboratorWorkspace,
        isWorkspaceSwitching,
        
        // NOVAS FUNÇÕES para melhor handling de cancelamento
        isSubscriptionCanceledButActive,
        getSubscriptionStatusMessage,
        canCancelSubscription,
        
        // Status flags úteis
        subscriptionFlags: {
          isActive: subscription?.active || false,
          isCanceled: subscription?.isCanceled || false,
          isExpired: subscription?.isExpired || false,
          isActiveButCanceled: subscription?.isActiveButCanceled || false,
          willCancelAtPeriodEnd: subscription?.willCancelAtPeriodEnd || false,
          userRequestedCancellation: subscription?.userRequestedCancellation || false,
          daysRemaining: subscription?.daysRemaining || 0
        }
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;